/**
 * Recruiting → Tasks page.
 *
 * Redesigned to mirror Intelligence → Action Plan:
 *   - Stats cards row (Open / Overdue / Due Today / Done this week)
 *   - AI Task Wizard ("✦ AI Wizard" button — purple)
 *   - Polished task cards with entity linking (candidate/job/client)
 *   - Filter row (status, task_type, assignee, show done)
 *
 * Data model is the same recruiter_tasks table — we didn't change that,
 * just the UX + added an AI draft endpoint for the wizard.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../lib/auth';
import { tasksApi, usersApi, RecruiterTask, OrgUser } from '../lib/api';
import { extractApiError } from '../lib/apiErrors';
import { useToast } from '../components/ToastHost';
import RecruiterTaskWizard from '../components/Tasks/RecruiterTaskWizard';
import Icon, { type IconName } from '../components/ui/Icon';

const TASK_TYPE_META: Record<string, { label: string; icon: IconName; color: string; bg: string }> = {
  call:      { label: 'Call',      icon: 'phone',    color: 'var(--info-fg)', bg: 'var(--info-tint)' },
  meeting:   { label: 'Meeting',   icon: 'calendar', color: 'var(--ai)', bg: 'var(--ai-tint)' },
  email:     { label: 'Email',     icon: 'mail',     color: 'var(--good-fg)', bg: 'var(--good-tint)' },
  sms:       { label: 'SMS',       icon: 'message',  color: 'var(--danger-fg)', bg: 'var(--danger-tint)' },
  follow_up: { label: 'Follow-up', icon: 'refresh',  color: 'var(--warn-fg)', bg: 'var(--warn-tint)' },
  todo:      { label: 'To-do',     icon: 'clipboard', color: 'var(--text)', bg: 'var(--surface-subtle)' },
  other:     { label: 'Other',     icon: 'tag',      color: 'var(--text-muted)', bg: 'var(--surface-subtle)' },
};

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function hoursUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return Math.round((d.getTime() - Date.now()) / 3_600_000);
}

export function TasksPanel({ embedded = false }: { embedded?: boolean }) {
  const nav = useNavigate();
  const toast = useToast();
  const { user: me } = useUser();

  const [tasks, setTasks] = useState<RecruiterTask[]>([]);
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<'open' | 'overdue' | 'due_today' | 'done' | 'all'>('open');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [assigneeFilter, setAssigneeFilter] = useState<string>('');

  // Modal state
  const [showWizard, setShowWizard] = useState(false);
  const [showManual, setShowManual] = useState(false);

  // Load users once — needed for assignee dropdown + rendering assignee names.
  useEffect(() => {
    void usersApi.directory().then(r => setUsers(r.data.users)).catch(() => { /* silent */ });
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params: Record<string, string> = {};
      if (statusFilter === 'overdue') params.overdue = 'true';
      else if (statusFilter === 'due_today') params.due_today = 'true';
      else if (statusFilter === 'done') params.status = 'done';
      else if (statusFilter === 'open') params.status = 'open';
      if (assigneeFilter) params.assigned_to = assigneeFilter;
      const res = await tasksApi.list(params);
      let rows = res.data.tasks;
      if (typeFilter) rows = rows.filter(t => t.task_type === typeFilter);
      setTasks(rows);
    } catch (e) {
      setError(extractApiError(e, 'Failed to load tasks'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, assigneeFilter]);

  useEffect(() => { void load(); }, [load]);

  // Stats — all computed on the current unfiltered view (by hitting the API
  // again with different filters would be chatty; instead we fetch all open
  // once and derive from that if statusFilter === 'open', otherwise fall
  // back to the current rows). Simpler + correct-enough: stats reflect the
  // tasks currently visible, plus we also fetch an "all" snapshot for the
  // big-picture counts.
  const [allOpen, setAllOpen] = useState<RecruiterTask[]>([]);
  useEffect(() => {
    void tasksApi.list({ status: 'open' }).then(r => setAllOpen(r.data.tasks)).catch(() => { /* silent */ });
  }, [tasks.length]); // re-pull after any mutation reflected in tasks

  const stats = useMemo(() => {
    const now = Date.now();
    const todayISO = new Date().toISOString().slice(0, 10);
    const weekAgo = now - 7 * 24 * 3_600_000;
    const overdue = allOpen.filter(t => t.due_at && new Date(t.due_at).getTime() < now).length;
    const dueToday = allOpen.filter(t => t.due_at?.slice(0, 10) === todayISO).length;
    const open = allOpen.length;
    const doneThisWeek = tasks.filter(t => t.status === 'done' && t.completed_at && new Date(t.completed_at).getTime() >= weekAgo).length;
    return { open, overdue, dueToday, doneThisWeek };
  }, [allOpen, tasks]);

  async function completeTask(id: string) {
    try {
      await tasksApi.complete(id);
      toast.success('Task completed');
      await load();
    } catch (e) {
      toast.error(extractApiError(e, 'Failed to complete task'));
    }
  }

  async function cancelTask(id: string) {
    if (!window.confirm('Cancel this task?')) return;
    try {
      await tasksApi.cancel(id);
      await load();
    } catch (e) {
      toast.error(extractApiError(e, 'Failed'));
    }
  }

  const contextLabel = (t: RecruiterTask): { label: string; icon: IconName; href: string | null } | null => {
    if (t.candidate_name && t.candidate_id) return { label: t.candidate_name, icon: 'user',     href: `/candidates/${t.candidate_id}` };
    if (t.job_title && t.job_id)             return { label: t.job_title,      icon: 'jobs',     href: `/jobs/${t.job_id}` };
    if (t.client_name && t.client_id)        return { label: t.client_name,    icon: 'building', href: `/clients-orgs/${t.client_id}` };
    if (t.submission_id)                     return { label: 'Submission',     icon: 'upload',   href: `/submissions/${t.submission_id}` };
    return null;
  };

  return (
    <div style={embedded ? undefined : { padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div>
          {!embedded && (
            <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-strong)', margin: 0, display: 'flex', alignItems: 'center', gap: 9 }}>
              <Icon name="clipboard" size={23} /> Recruiter Tasks
            </h1>
          )}
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '2px 0 0' }}>Calls, meetings, follow-ups — stay on top of your candidates and clients</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowManual(true)} style={ghostBtn}>+ Task</button>
          <button
            onClick={() => setShowWizard(true)}
            style={{ padding: '9px 18px', background: 'var(--ai)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="sparkles" size={14} /> AI Wizard</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 18 }}>
        <Stat label="Open" value={stats.open} color="var(--brand)" onClick={() => setStatusFilter('open')} />
        <Stat label="Overdue" value={stats.overdue} color="var(--danger-fg)" onClick={() => setStatusFilter('overdue')} />
        <Stat label="Due today" value={stats.dueToday} color="var(--warn)" onClick={() => setStatusFilter('due_today')} />
        <Stat label="Done this week" value={stats.doneThisWeek} color="var(--good)" onClick={() => setStatusFilter('done')} />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <select style={filter} value={statusFilter} onChange={e => setStatusFilter(e.target.value as typeof statusFilter)}>
          <option value="open">Open</option>
          <option value="overdue">Overdue</option>
          <option value="due_today">Due today</option>
          <option value="done">Done</option>
          <option value="all">All</option>
        </select>
        <select style={filter} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All types</option>
          {Object.entries(TASK_TYPE_META).map(([k, m]) => (
            <option key={k} value={k}>{m.label}</option>
          ))}
        </select>
        <select style={filter} value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)}>
          <option value="">All assignees</option>
          {me && (() => {
            const meRow = users.find(u => u.clerk_user_id === me.id);
            return meRow ? <option value={meRow.id}>Mine</option> : null;
          })()}
          {users.map(u => (
            <option key={u.id} value={u.id}>{u.name ?? u.email}</option>
          ))}
        </select>
      </div>

      {error && <div style={{ background: 'var(--danger-tint)', border: '1px solid var(--danger-border)', color: 'var(--danger-fg)', padding: 10, borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{error}</div>}

      {/* Empty / loading / list */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
      ) : tasks.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ marginBottom: 8, color: 'var(--good)', display: 'flex', justifyContent: 'center' }}><Icon name="check-circle" size={30} /></div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-strong)' }}>No tasks here</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
            Click AI Wizard to draft one in seconds, or + Task for manual entry.
          </div>
          <button onClick={() => setShowWizard(true)} style={{ padding: '9px 20px', background: 'var(--ai)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="sparkles" size={13} /> Start with AI</span>
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {tasks.map(t => {
            const meta = TASK_TYPE_META[t.task_type ?? 'todo'] ?? TASK_TYPE_META.todo;
            const hours = hoursUntil(t.due_at);
            const overdue = t.status === 'open' && hours != null && hours < 0;
            const dueSoon = t.status === 'open' && hours != null && hours >= 0 && hours <= 24;
            const ctx = contextLabel(t);
            const assignee = users.find(u => u.id === t.assigned_to);
            return (
              <div
                key={t.id}
                style={{ padding: 14, background: 'var(--surface)', border: '1px solid ' + (overdue ? '#fca5a5' : dueSoon ? 'var(--warn-tint)' : 'var(--border)'), borderRadius: 10, opacity: t.status === 'done' || t.status === 'cancelled' ? 0.55 : 1 }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <input
                    type="checkbox"
                    checked={t.status === 'done'}
                    onChange={() => void completeTask(t.id)}
                    disabled={t.status !== 'open'}
                    style={{ width: 18, height: 18, marginTop: 2, accentColor: 'var(--good)', cursor: t.status === 'open' ? 'pointer' : 'not-allowed' }}
                    title={t.status === 'open' ? 'Mark complete' : `Already ${t.status}`}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-strong)', textDecoration: t.status === 'done' ? 'line-through' : 'none' }}>
                        {t.title}
                      </span>
                      {t.is_private && (
                        <span title="Private — only you and the assignee can see this" style={{ padding: '2px 8px', borderRadius: 10, background: 'var(--warn-tint)', color: 'var(--warn-fg)', fontSize: 10, fontWeight: 700 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="lock" size={11} /> Private</span>
                        </span>
                      )}
                      {t.recurrence && (
                        <span title={`Repeats ${t.recurrence}`} style={{ padding: '2px 8px', borderRadius: 10, background: '#e0e7ff', color: '#3730a3', fontSize: 10, fontWeight: 700 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="refresh" size={11} /> {t.recurrence}</span>
                        </span>
                      )}
                      <span style={{ padding: '2px 8px', borderRadius: 10, background: meta.bg, color: meta.color, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                        <Icon name={meta.icon} size={11} /> {meta.label}
                      </span>
                      {ctx && (
                        ctx.href ? (
                          <a
                            href={ctx.href}
                            onClick={e => { e.preventDefault(); if (ctx.href) nav(ctx.href); }}
                            style={{ padding: '2px 8px', borderRadius: 10, background: 'var(--info-tint)', color: 'var(--brand)', fontSize: 10, fontWeight: 700, textDecoration: 'none' }}
                          >
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name={ctx.icon} size={10} /> {ctx.label}</span>
                          </a>
                        ) : (
                          <span style={{ padding: '2px 8px', borderRadius: 10, background: 'var(--info-tint)', color: 'var(--brand)', fontSize: 10, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name={ctx.icon} size={10} /> {ctx.label}</span>
                        )
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      {t.due_at && (
                        <span style={{ color: overdue ? 'var(--danger-fg)' : dueSoon ? 'var(--warn)' : 'var(--text-muted)', fontWeight: overdue || dueSoon ? 600 : 400 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="calendar" size={11} /> {fmtDateTime(t.due_at)}</span>
                          {overdue ? ' (overdue)' : dueSoon && hours != null ? ` (${hours >= 0 ? hours : 0}h)` : ''}
                        </span>
                      )}
                      {assignee && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="user" size={11} /> {assignee.name ?? assignee.email}</span>}
                      {t.description && <span style={{ color: 'var(--text-muted)' }}>· {t.description.slice(0, 80)}{t.description.length > 80 ? '…' : ''}</span>}
                    </div>
                    {t.barrier_note && (
                      <div style={{ marginTop: 6, padding: '4px 8px', background: 'var(--warn-tint)', border: '1px solid var(--warn-border)', borderRadius: 4, fontSize: 12, color: 'var(--warn-fg)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4 }}>Blocker:</span>
                        <span>{t.barrier_note}</span>
                      </div>
                    )}
                  </div>
                  {t.status === 'open' && (
                    <button
                      onClick={() => void cancelTask(t.id)}
                      style={{ background: 'none', border: 'none', color: 'var(--danger)', fontSize: 16, cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}
                      title="Cancel task"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {showWizard && (
        <RecruiterTaskWizard
          onCreated={() => { void load(); }}
          onClose={() => setShowWizard(false)}
        />
      )}
      {showManual && (
        <ManualTaskModal
          users={users}
          me={me?.id}
          onClose={() => setShowManual(false)}
          onSaved={() => { setShowManual(false); void load(); }}
        />
      )}
    </div>
  );
}

// ─── Stat card ───────────────────────────────────────────────────────────
function Stat({ label, value, color, onClick }: { label: string; value: number; color: string; onClick?: () => void }) {
  return (
    <div
      onClick={onClick}
      style={{ padding: 14, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, cursor: onClick ? 'pointer' : 'default', transition: 'border-color 0.15s' }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.borderColor = color; }}
      onMouseLeave={e => { if (onClick) e.currentTarget.style.borderColor = 'var(--border)'; }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color, marginTop: 2 }}>{value}</div>
    </div>
  );
}

// ─── Manual entry modal (simple form, mirrors AI wizard review stage) ───
interface ManualProps {
  users: OrgUser[];
  me: string | null | undefined;
  onClose: () => void;
  onSaved: () => void;
}

function ManualTaskModal({ users, me, onClose, onSaved }: ManualProps) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [taskType, setTaskType] = useState<RecruiterTask['task_type']>('todo');
  const [dueAt, setDueAt] = useState('');
  const [description, setDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [recurrence, setRecurrence] = useState<string>('');
  const [assignedTo, setAssignedTo] = useState<string>(() => {
    const meRow = users.find(u => u.clerk_user_id === me);
    return meRow?.id ?? '';
  });

  async function save() {
    if (!title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      await tasksApi.create({
        title: title.trim(),
        task_type: taskType,
        due_at: dueAt ? new Date(dueAt).toISOString() : null,
        description: description.trim() || null,
        assigned_to: assignedTo || null,
        is_private: isPrivate,
        recurrence: recurrence || null,
      });
      toast.success('Task created');
      onSaved();
    } catch (e) {
      toast.error(extractApiError(e, 'Failed to create task'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>New Task</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Manual entry — or try the AI Wizard</div>
          </div>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={lbl}>Title</label>
            <input autoFocus style={field} value={title} onChange={e => setTitle(e.target.value)} placeholder="Task title…" />
          </div>
          <div>
            <label style={lbl}>Type</label>
            <select style={field} value={taskType ?? 'todo'} onChange={e => setTaskType(e.target.value as RecruiterTask['task_type'])}>
              {Object.entries(TASK_TYPE_META).map(([k, m]) => (
                <option key={k} value={k}>{m.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={lbl}>Due</label>
            <input type="datetime-local" style={field} value={dueAt} onChange={e => setDueAt(e.target.value)} />
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={lbl}>Repeat</label>
            <select style={field} value={recurrence} onChange={e => setRecurrence(e.target.value)}>
              <option value="">Never (one-time task)</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Every 2 weeks</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </select>
            {recurrence && (
              <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>
                When you complete this task, the next instance will be created automatically.
              </div>
            )}
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={lbl}>Assign to</label>
            <select style={field} value={assignedTo} onChange={e => setAssignedTo(e.target.value)}>
              <option value="">— Unassigned —</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name ?? u.email}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={lbl}>Description</label>
            <textarea
              style={{ ...field, minHeight: 60, resize: 'vertical' }}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Optional context / notes"
            />
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: isPrivate ? 'var(--warn-tint)' : 'var(--surface-sunken)', border: '1px solid', borderColor: isPrivate ? 'var(--warn-tint)' : 'var(--border)', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}>
              <input
                type="checkbox"
                checked={isPrivate}
                onChange={e => setIsPrivate(e.target.checked)}
                style={{ accentColor: 'var(--warn-fg)' }}
              />
              <span style={{ fontWeight: 600, color: isPrivate ? 'var(--warn-fg)' : 'var(--text)' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="lock" size={12} /> Private — only you (and the assignee) can see this</span>
              </span>
            </label>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button onClick={onClose} style={ghostBtn}>Cancel</button>
          <button onClick={() => void save()} style={primaryBtn} disabled={saving || !title.trim()}>
            {saving ? 'Saving…' : 'Create task'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Styles (shared with RecruiterTaskWizard but self-contained here) ────
const ghostBtn: React.CSSProperties = { padding: '9px 18px', background: 'var(--surface-subtle)', border: '1px solid var(--border)', borderRadius: 8, fontWeight: 600, fontSize: 13, color: 'var(--text)', cursor: 'pointer' };
const filter: React.CSSProperties = { padding: '7px 10px', border: '1.5px solid var(--border)', borderRadius: 7, fontSize: 13, background: 'var(--surface)', cursor: 'pointer' };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const card: React.CSSProperties = { background: 'var(--surface)', borderRadius: 14, padding: 22, width: '100%', maxWidth: 560, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' };
const field: React.CSSProperties = { width: '100%', padding: '7px 10px', border: '1.5px solid var(--border)', borderRadius: 7, fontSize: 13, outline: 'none', boxSizing: 'border-box', background: 'var(--surface)', fontFamily: 'inherit', color: 'var(--text-strong)' };
const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 };
const primaryBtn: React.CSSProperties = { padding: '9px 18px', background: 'var(--ai)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' };
const closeBtn: React.CSSProperties = { background: 'none', border: 'none', fontSize: 22, color: 'var(--text-muted)', cursor: 'pointer' };


// Standalone route (/tasks) — deep links from task emails and bell
// notifications land here; the same panel is embedded as the Tasks tab of
// Candidate Updates / Communications.
export default function Tasks() {
  return <TasksPanel />;
}
